import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Badge } from '@/components/ui/badge';
import { User, ChevronDown, X, UserPlus } from 'lucide-react';
import { getPageAccess } from '@/lib/AuthSession';

/**
 * PicSelector — Komponen pilih PIC yang terintegrasi dengan data TeamMember (Manajemen Tim).
 *
 * Props:
 *   value       — nama PIC yang dipilih (string, full_name dari TeamMember)
 *   onChange    — (name: string) => void
 *   label       — label atas (default: "Penanggung Jawab (PIC)")
 *   placeholder — placeholder input (default: "Cari dan pilih orang...")
 *   allowedRoles — array role yang boleh dipilih (default: semua kecuali developer/owner)
 */

const DEFAULT_ALLOWED_ROLES = [
  'admin',
  'kepala',
  'staff',
];

const DEFAULT_ROLE_LABELS = {
  admin: 'Admin',
  kepala: 'Kepala',
  staff: 'Staff',
};

export default function PicSelector({
  value,
  onChange,
  label = 'Penanggung Jawab (PIC)',
  placeholder = 'Cari dan pilih orang...',
  allowedRoles = DEFAULT_ALLOWED_ROLES,
  roleLabelMap = DEFAULT_ROLE_LABELS,
  requiredPageAccessKey = null,
  requiredPicRole = null,
  picType = null, // "marketing" | "task"
  taskType = null, // "mixing" | "filling" | "pengemasan" | "packing"
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const containerRef = useRef(null);

  const getMemberPriority = (m, taskKindProp) => {
    if (!taskKindProp) return 0;
    const taskKind = String(taskKindProp).toLowerCase();
    
    const r = (m.role || '').toLowerCase();
    const pos = (m.position || '').toLowerCase();
    const dep = (m.department || '').toLowerCase();
    const isKepalaText = `${r} ${pos} ${dep}`.includes('kepala') || `${r} ${pos} ${dep}`.includes('lead') || `${r} ${pos} ${dep}`.includes('spv');

    const isProdTask = ['mixing', 'filling', 'produksi'].some(k => taskKind.includes(k));
    const isPackTask = ['pengemasan', 'packing', 'packaging'].some(k => taskKind.includes(k));

    if (isProdTask) {
      // Role explicitly specifies Kepala Produksi -> TOP #1 (Priority 50)
      if (r.includes('kepala') && r.includes('produksi')) return 50;
      // Position explicitly specifies Kepala Produksi -> TOP #1 (Priority 45)
      if (pos.includes('kepala') && pos.includes('produksi')) return 45;
      // Role specifies Staff Produksi -> URUTAN #2 (Priority 30)
      if (r.includes('staff') && r.includes('produksi')) return 30;
      // General Produksi staff/dept (Priority 20)
      if (r.includes('produksi') || pos.includes('produksi') || dep.includes('produksi')) return 20;
      if (isKepalaText) return 10;
      return 0;
    }

    if (isPackTask) {
      // Role explicitly specifies Kepala Pengemasan -> TOP #1 (Priority 50)
      if (r.includes('kepala') && (r.includes('pengemasan') || r.includes('packing'))) return 50;
      // Position explicitly specifies Kepala Pengemasan -> TOP #1 (Priority 45)
      if (pos.includes('kepala') && (pos.includes('pengemasan') || pos.includes('packing'))) return 45;
      // Role specifies Staff Pengemasan -> URUTAN #2 (Priority 30)
      if (r.includes('staff') && (r.includes('pengemasan') || r.includes('packing'))) return 30;
      // General Pengemasan staff/dept (Priority 20)
      if (r.includes('pengemasan') || pos.includes('pengemasan') || dep.includes('pengemasan')) return 20;
      if (isKepalaText) return 10;
      return 0;
    }

    return 0;
  };

  // Ambil SEMUA member (aktif & nonaktif) agar bisa deteksi jika PIC tersimpan tapi sudah nonaktif
  const { data: allMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => base44.entities.TeamMember.list(),
  });

  // Hanya yang aktif & sesuai requiredPageAccessKey, picType, atau allowedRoles
  const eligibleMembers = useMemo(
    () => allMembers.filter(m => {
      const statusStr = (m.status || 'aktif').toLowerCase();
      if (statusStr !== 'aktif' && statusStr !== 'active' && statusStr !== 'verified') return false;

      // Blok developer & non-operasional khusus
      if (['developer'].includes(m.role)) return false;

      const pos = (m.position || '').toLowerCase();
      const dep = (m.department || '').toLowerCase();
      const r = (m.role || '').toLowerCase();

      if (pos.includes('developer') || dep.includes('developer')) {
        return false;
      }

      let pa = m.page_access;
      if (typeof pa === 'string') {
        try { pa = JSON.parse(pa); } catch (e) { pa = {}; }
      }
      pa = pa || {};

      // MODE 1: PIC MARKETING (Penanggung Jawab PO Order)
      if (picType === 'marketing' || requiredPageAccessKey === 'po_produksi') {
        if (pa.po_produksi_is_pic || pa.po_produksi_allow_pic || pa.po_produksi_pic) {
          return true;
        }
        if (dep.includes('marketing') || pos.includes('marketing') || r === 'marketing' || r === 'admin' || r === 'owner') {
          return true;
        }
        return false;
      }

      // MODE 2: PIC TUGAS PRODUKSI (Operator Tugas Saya)
      if (picType === 'task' || requiredPageAccessKey === 'tugas_saya') {
        const picVal = pa.tugas_saya_is_pic || pa.tugas_saya_allow_pic || pa.tugas_saya_pic;
        if (picVal) {
          return true;
        }
        if (dep.includes('produksi') || dep.includes('pengemasan') || dep.includes('qc') || pos.includes('produksi') || pos.includes('pengemasan') || pos.includes('qc') || r === 'admin' || r === 'owner') {
          return true;
        }
        return false;
      }

      // MODE 3: PIC DELIVERY (Form Pengiriman)
      if (picType === 'delivery' || requiredPageAccessKey === 'delivery') {
        if (pa.delivery_allow_pic || pa.delivery_is_pic || pa.delivery_pic) {
          return true;
        }
        if (dep.includes('delivery') || dep.includes('logistik') || dep.includes('pengiriman') || pos.includes('delivery') || pos.includes('logistik') || pos.includes('pengiriman') || pos.includes('kurir') || pos.includes('driver')) {
          return true;
        }
        return false;
      }

      // MODE 4: PIC SAMPLE RACIKAN (Brand vs Pembuat vs Keduanya)
      if (requiredPageAccessKey === 'sample_racikan') {
        const picVal = pa.sample_racikan_is_pic || pa.sample_racikan_allow_pic || pa.sample_racikan_pic;
        if (requiredPicRole === 'brand') {
          if (picVal === 'brand' || picVal === 'both' || picVal === true) return true;
          if (dep.includes('marketing') || pos.includes('marketing') || r === 'marketing' || r === 'admin' || r === 'owner') return true;
          return false;
        }
        if (requiredPicRole === 'pembuat') {
          if (picVal === 'pembuat' || picVal === 'both' || picVal === true) return true;
          if (dep.includes('produksi') || dep.includes('lab') || dep.includes('rd') || dep.includes('r&d') || pos.includes('formulator') || pos.includes('racik') || pos.includes('lab') || pos.includes('produksi') || r === 'admin' || r === 'owner') return true;
          return false;
        }
        if (picVal) return true;
        return false;
      }

      // DEFAULT MODE: Sambungkan ke requiredPageAccessKey generik jika ada
      if (requiredPageAccessKey) {
        if (pa[`${requiredPageAccessKey}_allow_pic`] || pa[`${requiredPageAccessKey}_is_pic`] || pa[`${requiredPageAccessKey}_pic`]) {
          return true;
        }
        return false;
      }

      // General fallback
      if (allowedRoles === 'all') return true;
      if (Array.isArray(allowedRoles)) {
        if (allowedRoles.includes(m.role) || allowedRoles.includes(r)) return true;
      }

      return true;
    }),
    [allMembers, allowedRoles, requiredPageAccessKey, requiredPicRole, picType]
  );

  // Semua member untuk lookup (termasuk nonaktif, agar bisa tampilkan badge nonaktif)
  const members = allMembers;

  const availableRoles = useMemo(() => {
    const roles = new Set(eligibleMembers.map(m => m.role));
    if (allowedRoles === 'all') return Array.from(roles);
    
    // Return allowed roles that have members, PLUS any extra roles that bypassed the filter (like owner)
    const defaultRoles = allowedRoles.filter(r => roles.has(r));
    const extraRoles = Array.from(roles).filter(r => !allowedRoles.includes(r));
    return [...defaultRoles, ...extraRoles];
  }, [eligibleMembers, allowedRoles]);

  const filteredMembers = useMemo(() => {
    return eligibleMembers.filter(m => {
      const matchRole = !filterRole || m.role === filterRole;
      const q = search.toLowerCase();
      const matchSearch =
        !search ||
        (m.full_name || '').toLowerCase().includes(q) ||
        (m.position || '').toLowerCase().includes(q) ||
        (m.department || '').toLowerCase().includes(q);
      return matchRole && matchSearch;
    });
  }, [eligibleMembers, filterRole, search]);

  const sortedFilteredMembers = useMemo(() => {
    const list = [...filteredMembers];
    return list.sort((a, b) => {
      const prioA = getMemberPriority(a, taskType);
      const prioB = getMemberPriority(b, taskType);
      if (prioA !== prioB) {
        return prioB - prioA; // Higher priority (matching task role) appears AT THE TOP!
      }
      return (a.full_name || '').localeCompare(b.full_name || '');
    });
  }, [filteredMembers, taskType]);

  // Match by full_name (dari semua member termasuk nonaktif)
  const selectedMember = members.find(m => m.full_name === value);
  const isSelectedInactive = selectedMember && selectedMember.status !== 'aktif';

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (member) => {
    onChange(member.full_name, member);
    setOpen(false);
    setSearch('');
    setFilterRole('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSearch('');
  };

  return (
    <div className="space-y-1.5" ref={containerRef}>
      {label && (
        <label className="text-sm font-medium leading-none">{label}</label>
      )}

      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(v => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors text-left ${
          disabled ? 'opacity-70 cursor-not-allowed bg-muted/50 border-input' :
          open ? 'border-primary ring-1 ring-ring' : 'border-input hover:border-primary/50'
        } bg-transparent`}
      >
        {selectedMember ? (
          <>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isSelectedInactive ? 'bg-red-100' : 'bg-primary/10'}`}>
              <User className={`w-3.5 h-3.5 ${isSelectedInactive ? 'text-red-400' : 'text-primary'}`} />
            </div>
            <span className={`flex-1 font-medium truncate ${isSelectedInactive ? 'text-muted-foreground line-through' : ''}`}>
              {selectedMember.asa_nickname ? selectedMember.asa_nickname : selectedMember.full_name}
              {selectedMember.asa_nickname && (
                <span className="text-xs opacity-60 ml-1.5 font-normal">({selectedMember.full_name})</span>
              )}
              {selectedMember.position ? ` · ${selectedMember.position}` : ''}
            </span>
            {isSelectedInactive ? (
              <Badge className="text-[10px] shrink-0 bg-red-100 text-red-600 border-red-200">Nonaktif</Badge>
            ) : (
              (!selectedMember.position || selectedMember.position.toLowerCase() !== (roleLabelMap[selectedMember.role] || selectedMember.role || '').toLowerCase()) && (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {roleLabelMap[selectedMember.role] || selectedMember.role}
                </Badge>
              )
            )}
            {!disabled && (
              <div
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => { if (e.key === 'Enter') handleClear(e); }}
                className="text-muted-foreground hover:text-destructive transition-colors ml-1 shrink-0 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </div>
            )}
          </>
        ) : value ? (
          // Nilai lama (email atau nama) yang tidak cocok dengan member mana pun
          <>
            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
              <User className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
            <span className="flex-1 truncate text-foreground">{value}</span>
            {!disabled && (
              <div
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => { if (e.key === 'Enter') handleClear(e); }}
                className="text-muted-foreground hover:text-destructive transition-colors ml-1 shrink-0 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </div>
            )}
          </>
        ) : (
          <>
            <User className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="flex-1 text-muted-foreground">{placeholder}</span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="border rounded-xl bg-background shadow-lg z-50 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Ketik untuk cari nama..."
              className="w-full text-sm px-3 py-1.5 rounded-lg border border-input focus:outline-none focus:ring-1 focus:ring-ring bg-transparent"
            />
          </div>

          {/* Role filter chips — selalu tampil */}
          {availableRoles.length >= 0 && (
            <div className="flex flex-wrap gap-1.5 px-2 py-2 border-b bg-muted/20">
              <button
                type="button"
                onClick={() => setFilterRole('')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  !filterRole
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                Semua
              </button>
              {availableRoles.map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setFilterRole(filterRole === r ? '' : r)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                    filterRole === r
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {roleLabelMap[r] || r}
                </button>
              ))}
            </div>
          )}

          {/* Member list */}
          <div className="max-h-48 overflow-y-auto">
            {eligibleMembers.length === 0 ? (
              <div className="flex flex-col items-center gap-1 py-6 text-muted-foreground">
                <UserPlus className="w-5 h-5 opacity-40" />
                <p className="text-xs">Belum ada anggota tim aktif</p>
              </div>
            ) : sortedFilteredMembers.length === 0 ? (
              <div className="flex flex-col items-center gap-1 py-6 text-muted-foreground">
                <UserPlus className="w-5 h-5 opacity-40" />
                <p className="text-xs">Tidak ditemukan</p>
              </div>
            ) : (
              sortedFilteredMembers.map(m => {
                const priority = getMemberPriority(m, taskType);
                const taskKind = (taskType || '').toLowerCase();
                const isProdTask = ['mixing', 'filling', 'produksi'].some(k => taskKind.includes(k));

                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => handleSelect(m)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-muted/60 transition-colors text-left text-sm ${
                      value === m.full_name ? 'bg-primary/5 font-semibold' : ''
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                      value === m.full_name
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {(m.full_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate flex items-baseline gap-1.5">
                        {m.asa_nickname ? m.asa_nickname : m.full_name}
                        {m.asa_nickname && (
                          <span className="text-[11px] text-muted-foreground font-normal">({m.full_name})</span>
                        )}
                      </p>
                      {m.position && <p className="text-xs text-muted-foreground truncate">{m.position}</p>}
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0 font-normal">
                      {roleLabelMap[m.role] || m.role}
                    </Badge>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-3 py-2 text-center">
            <p className="text-[10px] text-muted-foreground">
              Tambah anggota tim di halaman <strong>Manajemen Tim</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}